import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Typography, Box, Chip, Grid, Card, CardMedia, CardContent, Stack, Button, CardActions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import portfolio from '../data/portfolioData';

export default function Portfolio() {
  const [filter, setFilter] = React.useState('All');

  const categories = ['All', ...Array.from(new Set(portfolio.map(p => p.category)))];
  const industries = ['All Industries', ...Array.from(new Set(portfolio.map(p => p.industry)))];

  const filtered = filter === 'All' ? portfolio : portfolio.filter(it => it.category === filter);

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
      minHeight: '100vh',
      py: 8
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2
          }
        }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Portfolio & Case Studies
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: 800, 
              mx: 'auto',
              lineHeight: 1.8,
              fontWeight: 400,
              mb: 4
            }}
          >
            Discover how we've helped leading organizations transform their businesses with AI, data science, and modern software engineering. 
            From Fortune 500 enterprises to innovative startups, our solutions deliver measurable impact.
          </Typography>

          {/* Stats */}
          <Grid container spacing={3} sx={{ maxWidth: 800, mx: 'auto', mb: 5 }}>
            {[
              { number: '10+', label: 'Success Stories' },
              { number: '8', label: 'Industries' },
              { number: '95%', label: 'Client Satisfaction' }
            ].map((stat, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: '1px solid',
                  borderColor: alpha('#667eea', 0.2)
                }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Filter Chips */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          justifyContent: 'center', 
          mb: 5,
          flexWrap: 'wrap'
        }}>
          {categories.map(c => (
            <Chip 
              key={c} 
              label={c} 
              onClick={() => setFilter(c)} 
              clickable
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                background: c === filter 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'transparent',
                color: c === filter ? 'white' : 'text.primary',
                border: '1px solid',
                borderColor: c === filter ? 'transparent' : alpha('#667eea', 0.3),
                '&:hover': {
                  background: c === filter
                    ? 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)'
                    : alpha('#667eea', 0.08)
                }
              }}
            />
          ))}
        </Box>

        {/* Portfolio Grid */}
        <Grid container spacing={4}>
          {filtered.map(p => (
            <Grid item xs={12} sm={6} md={4} key={p.slug}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                    borderColor: alpha('#667eea', 0.3)
                  }
                }} 
                component={Link} 
                to={`/portfolio/${p.slug}`}
              >
                {/* Image with Overlay */}
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                  <CardMedia 
                    component="img" 
                    height="220" 
                    image={p.image} 
                    alt={p.title}
                    sx={{
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5
                  }}>
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                      {p.industry}
                    </Typography>
                  </Box>
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {p.title}
                  </Typography>
                  
                  <Chip 
                    label={p.category} 
                    size="small" 
                    sx={{ 
                      mb: 2,
                      background: alpha('#667eea', 0.1),
                      color: '#667eea',
                      fontWeight: 600
                    }} 
                  />
                  
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary', 
                      mb: 2,
                      lineHeight: 1.7
                    }}
                  >
                    {p.summary}
                  </Typography>

                  {/* Metrics */}
                  {p.metrics && (
                    <Stack direction="row" spacing={2} sx={{ mt: 'auto' }}>
                      {Object.entries(p.metrics).slice(0, 2).map(([key, value]) => (
                        <Box key={key} sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#667eea' }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      color: '#667eea',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 1,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }
                    }}
                  >
                    View Case Study
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
          border: '1px solid',
          borderColor: alpha('#667eea', 0.2),
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 600, mx: 'auto' }}>
            Let's discuss how we can help you achieve similar results with AI, data science, and modern software engineering.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            component={Link}
            to="/contact"
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: '0 6px 24px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Start Your Project
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
